import { useState, useEffect } from 'react';
import { notesAPI } from '@/lib/api';
import { authHeaders } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  MessageSquare, 
  Plus, 
  Trash2, 
  Eye, 
  EyeOff, 
  Loader2,
  X,
  Lock,
  ChevronDown,
  ChevronUp,
  ZoomIn
} from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

function getAttachmentUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url}`;
}

async function fetchAttachmentBlobUrl(url) {
  const response = await fetch(getAttachmentUrl(url), {
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error('Attachment fetch failed');
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export default function EmployeeNotes({ employeeId, employeeName, isAdmin = false }) {
  const { t } = useLanguage();
  const [feed, setFeed] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [isVisible, setIsVisible] = useState(true); // visible to employee by default
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const [showHidden, setShowHidden] = useState(false);

  const LIMIT = 10;

  useEffect(() => {
    loadFeed(0, false);
  }, [employeeId]); // eslint-disable-line

  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
    };
  }, [blobUrls]);

  const loadFeed = async (fromSkip = 0, append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      const res = await notesAPI.feed(employeeId, fromSkip, LIMIT);
      const items = res.data || [];
      setFeed(prev => append ? [...prev, ...items] : items);
      setSkip(fromSkip + items.length);
      setHasMore(items.length === LIMIT);
    } catch (e) {
      console.error('Failed to load notes:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleCreate = async () => {
    if (!newContent.trim()) return;
    try {
      setSaving(true);
      await notesAPI.create({
        employee_id: employeeId,
        content: newContent.trim(),
        is_visible_to_employee: isVisible,
      });
      setNewContent('');
      setIsVisible(true);
      setShowAddForm(false);
      loadFeed(0, false);
    } catch (e) {
      console.error('Failed to create note:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Удалить эту заметку?')) return;
    try {
      await notesAPI.delete(noteId);
      setFeed(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      console.error('Failed to delete note:', e);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Удалить ВСЕ заметки сотрудника? Действие необратимо.')) return;
    try {
      await notesAPI.clearAll(employeeId);
      setFeed([]);
      setHasMore(false);
    } catch (e) {
      console.error('Failed to clear notes:', e);
    }
  };

  const ensureBlobUrl = async (att) => {
    if (!att?.id || !att?.url) return null;
    if (blobUrls[att.id]) return blobUrls[att.id];
    const blobUrl = await fetchAttachmentBlobUrl(att.url);
    setBlobUrls(prev => ({ ...prev, [att.id]: blobUrl }));
    return blobUrl;
  };

  const handleFileUpload = async (noteId, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert(t('notes.onlyImages') || 'Only images allowed (JPEG, PNG, GIF, WebP)');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      alert(t('notes.maxSize') || 'Max size: 15 MB');
      return;
    }
    try {
      setUploading(true);
      await notesAPI.uploadPhoto(noteId, file, new Date(file.lastModified).toISOString());
      loadFeed(0, false);
    } catch (e) {
      console.error('Failed to upload:', e);
    } finally {
      setUploading(false);
    }
  };

  // Split feed into visible (chat) and hidden (private admin notes)
  const visibleNotes = feed.filter(n => n.is_visible_to_employee || n.is_self_note);
  const hiddenNotes = feed.filter(n => !n.is_visible_to_employee && !n.is_self_note);

  const roleCls = (role) => role === 'employee'
    ? 'bg-emerald-500/20 text-emerald-400'
    : 'bg-indigo-500/20 text-indigo-300';

  const NoteCard = ({ note }) => (
    <div className={`glass-card rounded-xl p-4 ${note.is_visible_to_employee || note.is_self_note ? 'border-l-2 border-emerald-500/40' : 'border-l-2 border-amber-500/40'}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-medium text-white">{note.author_name}</span>
        {note.author_role && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleCls(note.author_role)}`}>
            {note.author_role === 'employee' ? t('notes.roleEmployee') : t('notes.roleManager')}
          </span>
        )}
        <span className="text-xs text-white/30 ml-auto">{format(new Date(note.created_at), 'dd.MM.yyyy HH:mm')}</span>
        {isAdmin && (
          <button onClick={() => handleDelete(note.id)} className="p-1 text-white/20 hover:text-rose-400 transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {note.content && <p className="text-white text-sm whitespace-pre-wrap mb-2">{note.content}</p>}

      {/* Attachments */}
      {note.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {note.attachments.map(att => {
            const isImage = att.content_type?.startsWith('image/') ||
              /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(att.original_filename || att.filename || '');
            return isImage ? (
              <div
                key={att.id}
                className="relative group cursor-pointer"
                onClick={async () => {
                  try {
                    const blobUrl = await ensureBlobUrl(att);
                    if (blobUrl) setPreviewImage(blobUrl);
                  } catch {}
                }}
              >
                <img
                  src={blobUrls[att.id] || getAttachmentUrl(att.url)}
                  alt={att.original_filename}
                  className="w-20 h-20 object-cover rounded-lg border border-wp-dark/60 hover:opacity-80 transition-opacity"
                  onLoadStart={async () => {
                    if (!blobUrls[att.id]) {
                      try { await ensureBlobUrl(att); } catch {}
                    }
                  }}
                  onError={async (e) => {
                    try {
                      const blobUrl = await ensureBlobUrl(att);
                      if (blobUrl) {
                        e.currentTarget.src = blobUrl;
                        return;
                      }
                    } catch {}
                    e.target.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                  <ZoomIn className="h-5 w-5 text-white drop-shadow" />
                </div>
              </div>
            ) : (
              <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-wp-light hover:text-white underline">
                {att.original_filename}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-wp-light" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-wp-light" />
          {employeeName ? `${t('notes.aboutEmployee')} ${employeeName}` : t('notes.journal')}
        </h3>
        <div className="flex items-center gap-2">
          {isAdmin && feed.length > 0 && (
            <button onClick={handleClearAll} className="text-xs text-rose-400/50 hover:text-rose-400 transition-colors flex items-center gap-1">
              <Trash2 className="h-3.5 w-3.5" />
              {t('notes.clearAll')}
            </button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={() => setShowAddForm(v => !v)} className="bg-indigo-600 hover:bg-indigo-500">
              <Plus className="h-4 w-4 mr-1" />
              {t('notes.add')}
            </Button>
          )}
        </div>
      </div>

      {/* Add Note Form */}
      {showAddForm && (
        <div className="glass-card rounded-xl p-4 space-y-3 border border-indigo-500/30">
          <Textarea
            value={newContent}
            onChange={e => setNewContent(e.target.value)}
            placeholder={t('notes.placeholder')}
            className="min-h-[90px] bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30"
            autoFocus
          />
          {/* Visibility toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={e => setIsVisible(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm flex items-center gap-1.5 text-white/70">
              {isVisible
                ? <><Eye className="h-4 w-4 text-emerald-400" /> {t('notes.visibleToEmployee')}</>
                : <><EyeOff className="h-4 w-4 text-amber-400" /> {t('notes.hiddenForManagement')}</>
              }
            </span>
          </label>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={saving || !newContent.trim()} className="bg-emerald-600 hover:bg-emerald-500">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('notes.save')}
            </Button>
            <Button variant="ghost" onClick={() => { setShowAddForm(false); setNewContent(''); }} className="text-white/60">
              <X className="h-4 w-4 mr-1" />{t('notes.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Hidden / Private Admin Notes — вверху */}
      {isAdmin && hiddenNotes.length > 0 && (
        <div>
          <button
            onClick={() => setShowHidden(v => !v)}
            className="flex items-center gap-2 text-xs text-amber-400/60 hover:text-amber-400 transition-colors mb-2"
          >
            <Lock className="h-3.5 w-3.5" />
            {t('notes.hiddenNotes')} ({hiddenNotes.length})
            {showHidden ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showHidden && (
            <div className="space-y-3">
              {hiddenNotes.map(n => <NoteCard key={n.id} note={n} />)}
            </div>
          )}
        </div>
      )}

      {/* Chat / Visible Notes */}
      <div>
        <p className="text-xs text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Eye className="h-3.5 w-3.5" /> {t('notes.journalLabel')}
        </p>
        {visibleNotes.length === 0 ? (
          <p className="text-white/30 text-sm text-center py-4">{t('notes.noEntries')}</p>
        ) : (
          <div className="space-y-3">
            {visibleNotes.map(n => <NoteCard key={n.id} note={n} />)}
          </div>
        )}
      </div>

      {/* Load more */}
      {hasMore && (
        <button onClick={() => loadFeed(skip, true)} disabled={loadingMore}
          className="w-full py-2 text-sm text-white/40 hover:text-white border border-wp-dark/40 hover:border-wp-dark/70 rounded-xl transition-all">
          {loadingMore ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('notes.showMore')}
        </button>
      )}

      {/* Image Preview */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl bg-slate-900 border-wp-dark/50 p-2">
          <DialogTitle className="sr-only">{t('notes.imagePreview')}</DialogTitle>
          {previewImage && (
            <img src={previewImage} alt="Preview" className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
